import { ElementType, HTMLAttributes, useEffect, useRef } from 'react';

// Scroll-reveal primitive matching the mockup's .rv / .rv.in behavior:
// content rises 34px and fades in the first time it enters the viewport.
// The .rv / .in / .d1-.d3 classes are defined in GlobalStyle.

/** Visible height (px) that reveals a block regardless of its ratio. */
const MIN_VISIBLE_PX = 80;

interface RevealProps extends HTMLAttributes<HTMLElement> {
	/** stagger step: 1 = +0.08s, 2 = +0.16s, 3 = +0.24s */
	delay?: 0 | 1 | 2 | 3;
	as?: ElementType;
}

export const Reveal = ({
	children,
	delay = 0,
	as: Tag = 'div',
	className = '',
	style,
	...rest
}: RevealProps) => {
	const ref = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		// Reveal once 12% of the element is visible, or once a fixed strip of
		// it is on screen — a block taller than the viewport (e.g. a one-column
		// card grid on mobile) can never reach a 12% ratio and would otherwise
		// stay hidden forever.
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (
						entry.isIntersecting &&
						(entry.intersectionRatio >= 0.12 ||
							entry.intersectionRect.height >= MIN_VISIBLE_PX)
					) {
						entry.target.classList.add('in');
						observer.unobserve(entry.target);
					}
				});
			},
			{ threshold: [0, 0.02, 0.04, 0.06, 0.08, 0.1, 0.12] }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<Tag
			ref={ref}
			className={`rv ${delay ? `d${delay}` : ''} ${className}`.trim()}
			style={style}
			{...rest}
		>
			{children}
		</Tag>
	);
};
